import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [dynamicPages, setDynamicPages] = useState([]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, pagesRes] = await Promise.all([
        axios.get(`${API}/settings/public`),
        axios.get(`${API}/pages/public`)
      ]);
      setSettings(settingsRes.data);
      setDynamicPages(pagesRes.data.filter(p => p.show_in_footer));
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const content = settings?.content || {};
  const footerColumns = settings?.footer_columns || [];
  const branding = settings?.branding || {};

  return (
    <footer className="bg-black border-t border-zinc-800/50">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4 group">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={content.site_title || 'Logo'}
                  style={{
                    width: branding.logo_width || 150,
                    height: branding.logo_height || 50,
                    objectFit: 'contain'
                  }}
                />
              ) : null}
              {(!branding.logo_url) && (
                <span className="text-2xl font-bold">
                  <span className="text-white">{content.site_title?.split(' ')[0] || 'Freelance'}</span>
                  <span className="text-emerald-500 group-hover:text-emerald-400 transition-colors">{content.site_title?.split(' ')[1] || 'Hub'}</span>
                </span>
              )}
            </Link>
            <p className="text-zinc-500 text-sm leading-relaxed mb-5">
              {content.footer_description || 'The most comprehensive freelancer platform. Find or offer professional services.'}
            </p>
            {/* App Download Links */}
            {(settings?.app_links?.android_url || settings?.app_links?.ios_url) && (
              <div className="flex items-center gap-3 mb-5">
                {settings.app_links.android_url && (
                  <a href={settings.app_links.android_url} target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-10" />
                  </a>
                )}
                {settings.app_links.ios_url && (
                  <a href={settings.app_links.ios_url} target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                    <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="App Store" className="h-10" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Dynamic Footer Columns */}
          {footerColumns.sort((a, b) => a.order - b.order).map(column => (
            <div key={column.id}>
              <h3 className="text-white font-semibold mb-4">{column.title}</h3>
              <ul className="space-y-2">
                {column.links.map((link, idx) => (
                  <li key={idx}>
                    {link.url.startsWith('/') ? (
                      <Link 
                        to={link.url}
                        className="text-zinc-500 hover:text-emerald-400 text-sm transition-colors hover:translate-x-0.5 inline-block"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a 
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-500 hover:text-emerald-400 text-sm transition-colors hover:translate-x-0.5 inline-block"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
                {/* Add dynamic pages for this column */}
                {dynamicPages
                  .filter(p => p.footer_column === column.order || (column.order === 3 && !p.footer_column))
                  .map(page => (
                    <li key={page.id}>
                      <Link 
                        to={`/page/${page.slug}`}
                        className="text-zinc-500 hover:text-emerald-400 text-sm transition-colors hover:translate-x-0.5 inline-block"
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))
                }
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-zinc-800/50">
        <div className="container mx-auto px-4 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-600 text-xs">
              © {new Date().getFullYear()} {content.site_title || ''}. {t('footer.copyright')}.
            </p>
            <div className="flex items-center gap-5">
              <Link to="/page/privacy" className="text-zinc-600 hover:text-emerald-400 text-xs transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/page/terms" className="text-zinc-600 hover:text-emerald-400 text-xs transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/blog" className="text-zinc-600 hover:text-emerald-400 text-xs transition-colors">
                {t('header.blog')}
              </Link>
              <Link to="/contact" className="text-zinc-600 hover:text-emerald-400 text-xs transition-colors">
                {t('footer.contact')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
